/**
 * Types mirroring the server's DTOs.
 *
 * Written by hand rather than generated. The API surface is small and stable, and a generator would
 * be one more tool that has to run before anyone can build — for the sake of types that change
 * roughly as often as the domain does.
 */

export const FuelType = {
  Petrol: 1,
  Diesel: 2,
  PetrolLpg: 3,
  PetrolCng: 4,
  Hybrid: 5,
  PlugInHybrid: 6,
  Electric: 7,
} as const;

export const ListingStatus = {
  Draft: 0,
  Active: 1,
  Sold: 2,
  Expired: 3,
  RemovedByUser: 4,
  RemovedByModerator: 5,
} as const;

export const SellerType = {
  Private: 1,
  Dealer: 2,
  Showroom: 3,
} as const;

/** Auto plac and auto salon publish company details; a private seller has none to publish. */
export function isBusinessSeller(sellerType: number): boolean {
  return sellerType === SellerType.Dealer || sellerType === SellerType.Showroom;
}

export const SortOrder = {
  Newest: 0,
  PriceAscending: 1,
  PriceDescending: 2,
  MileageAscending: 3,
  YearDescending: 4,
  YearAscending: 5,
} as const;

export interface EnumOption {
  value: number;
  name: string;
  label: string;
}

export interface EquipmentOption {
  value: number;
  name: string;
  label: string;
  group: string;
}

export interface Make {
  id: number;
  name: string;
  slug: string;
  isPopular: boolean;
}

export interface Model {
  id: number;
  makeId: number;
  name: string;
  slug: string;
}

export interface City {
  id: number;
  name: string;
  slug: string;
  region: string;
}

export interface ReferenceData {
  makes: Make[];
  models: Model[];
  cities: City[];
  colours: string[];
  equipment: EquipmentOption[];
  enums: {
    fuelTypes: EnumOption[];
    bodyTypes: EnumOption[];
    transmissions: EnumOption[];
    driveTypes: EnumOption[];
    conditions: EnumOption[];
    emissionClasses: EnumOption[];
    damageStatuses: EnumOption[];
    origins: EnumOption[];
    sellerTypes: EnumOption[];
    climateControls: EnumOption[];
    interiors: EnumOption[];
    steeringSides: EnumOption[];
    listingStatuses: EnumOption[];
  };
  yearRange: { min: number; max: number };
}

export interface User {
  id: string;
  email: string;
  displayName: string;
  profilePhotoUrl?: string | null;
  contactPhone?: string | null;
  phoneVerified: boolean;
  sellerType: number;
  dealershipName?: string | null;
  cityId?: number | null;
  bio?: string | null;
  businessAddress?: string | null;
  publicEmail?: string | null;
  websiteUrl?: string | null;
  businessHours?: string | null;
  taxNumber?: string | null;
  companyNumber?: string | null;
  showPhonePublicly: boolean;
}

/** The profile form's payload. Business fields are sent whatever the type; the server clears them. */
export interface UpdateProfileRequest {
  sellerType: number;
  displayName: string;
  bio?: string | null;
  cityId?: number | null;
  contactPhone?: string | null;
  showPhonePublicly: boolean;
  dealershipName?: string | null;
  businessAddress?: string | null;
  publicEmail?: string | null;
  websiteUrl?: string | null;
  businessHours?: string | null;
  taxNumber?: string | null;
  companyNumber?: string | null;
}

export interface AuthResponse {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken?: string | null;
  user: User;
}

export interface ListingSummary {
  id: string;
  shortCode: string;
  slug: string;
  makeName: string;
  modelName: string;
  variant?: string | null;
  year: number;
  mileage: number;
  price: number;
  previousPrice?: number | null;
  priceNegotiable: boolean;
  fuelType: number;
  transmission: number;
  bodyType: number;
  powerKw?: number | null;
  cityName: string;
  thumbnailUrl?: string | null;
  photoCount: number;
  sellerType: number;
  publishedAt: string;
  isFavourite: boolean;
  rating?: number | null;
  ratingCount: number;
}

export interface ListingPhoto {
  id: string;
  url: string;
  thumbnailUrl: string;
  sortOrder: number;
  width: number;
  height: number;
}

export interface ListingSeller {
  id: string;
  displayName: string;
  dealershipName?: string | null;
  profilePhotoUrl?: string | null;
  sellerType: number;
  contactPhone?: string | null;
  phoneVerified: boolean;
  memberSince: string;
  activeListingCount: number;
  rating?: number | null;
  ratingCount: number;
}

export interface ListingDetail {
  id: string;
  shortCode: string;
  slug: string;
  makeId: number;
  makeName: string;
  modelId: number;
  modelName: string;
  variant?: string | null;
  year: number;
  registrationMonth?: number | null;
  mileage: number;
  price: number;
  previousPrice?: number | null;
  priceNegotiable: boolean;
  fuelType: number;
  bodyType: number;
  transmission: number;
  driveType: number;
  powerKw?: number | null;
  powerHp?: number | null;
  engineCc?: number | null;
  doors?: number | null;
  seats?: number | null;
  emissionClass?: number | null;
  climate: number;
  interior?: number | null;
  steeringSide: number;
  colour?: string | null;
  metallicPaint: boolean;
  condition: number;
  damageStatus: number;
  origin: number;
  registeredUntil?: string | null;
  previousOwners?: number | null;
  serviceBook: boolean;
  firstOwner: boolean;
  vin?: string | null;
  sellerType: number;
  exchangeAccepted: boolean;
  financeAvailable: boolean;
  vatDeductible: boolean;
  equipmentMask: number;
  cityId: number;
  cityName: string;
  /** The number set on this advert, if one was. `seller.contactPhone` is the resolved value. */
  listingContactPhone?: string | null;
  description?: string | null;
  status: number;
  createdAt: string;
  publishedAt?: string | null;
  expiresAt?: string | null;
  viewCount: number;
  favouriteCount: number;
  isFavourite: boolean;
  isOwnedByCaller: boolean;
  rating?: number | null;
  ratingCount: number;
  photos: ListingPhoto[];
  seller: ListingSeller;
}

/* --------------------------------------------------------------------------------------------- */
/* Ratings and reviews                                                                            */
/* --------------------------------------------------------------------------------------------- */

export interface RatingBreakdown {
  one: number;
  two: number;
  three: number;
  four: number;
  five: number;
}

export interface RatingSummary {
  average?: number | null;
  count: number;
  breakdown: RatingBreakdown;
}

export interface ReviewAuthor {
  id: string;
  displayName: string;
  profilePhotoUrl?: string | null;
  memberSince: string;
}

export interface ListingReview {
  id: string;
  rating: number;
  comment?: string | null;
  createdAt: string;
  editedAt?: string | null;
  author: ReviewAuthor;
  isOwnedByCaller: boolean;
}

export interface SellerReview {
  id: string;
  rating: number;
  comment?: string | null;
  sellerReply?: string | null;
  sellerRepliedAt?: string | null;
  createdAt: string;
  editedAt?: string | null;
  author: ReviewAuthor;
  listingId?: string | null;
  listingShortCode?: string | null;
  listingTitle?: string | null;
  isOwnedByCaller: boolean;
  canReply: boolean;
}

export interface ReviewPage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: RatingSummary;
  /** The caller's own review, returned even when it falls outside the requested page. */
  mine?: T | null;
}

export interface SellerProfile {
  id: string;
  displayName: string;
  dealershipName?: string | null;
  profilePhotoUrl?: string | null;
  bio?: string | null;
  cityName?: string | null;
  sellerType: number;
  phoneVerified: boolean;
  memberSince: string;
  activeListingCount: number;
  soldListingCount: number;
  rating: RatingSummary;
  /** Absent unless the seller chose to publish it. */
  contactPhone?: string | null;
  businessAddress?: string | null;
  publicEmail?: string | null;
  websiteUrl?: string | null;
  businessHours?: string | null;
  taxNumber?: string | null;
  companyNumber?: string | null;
}

/* --------------------------------------------------------------------------------------------- */
/* Vehicle insight                                                                                */
/* --------------------------------------------------------------------------------------------- */

export interface VinInsight {
  isWellFormed: boolean;
  checkDigitValid: boolean;
  country?: string | null;
  manufacturerHint?: string | null;
  modelYear?: number | null;
  warnings: string[];
}

/** 0 strength, 1 minor, 2 notable, 3 serious. */
export interface VehicleNote {
  id: number;
  severity: number;
  title: string;
  description: string;
  typicalMileage?: number | null;
  pastTypicalMileage: boolean;
}

export interface RegistrationCostLine {
  label: string;
  amountRsd: number;
  note?: string | null;
}

export interface RegistrationCost {
  totalRsd: number;
  totalEur: number;
  lines: RegistrationCostLine[];
  tariffYear: number;
  isComplete: boolean;
  assumptions: string[];
}

/** 0 pending, 1 clean, 2 flagged, 3 unavailable, 4 mismatch. */
export interface VehicleHistory {
  status: number;
  provider: string;
  damageRecords?: number | null;
  mileageRollback?: boolean | null;
  highestRecordedMileage?: number | null;
  recordedOwners?: number | null;
  stolenRecord?: boolean | null;
  reportUrl?: string | null;
  checkedAt: string;
  isStale: boolean;
}

export interface VehicleInsight {
  vin?: VinInsight | null;
  notes: VehicleNote[];
  registration: RegistrationCost;
  history?: VehicleHistory | null;
  historyAvailable: boolean;
}

/* --------------------------------------------------------------------------------------------- */
/* Messaging                                                                                      */
/* --------------------------------------------------------------------------------------------- */

export interface ConversationSummary {
  id: string;
  listingId: string;
  listingShortCode: string;
  listingTitle: string;
  listingThumbnailUrl?: string | null;
  listingPrice: number;
  counterpartyId: string;
  counterpartyName: string;
  counterpartyPhotoUrl?: string | null;
  callerIsSeller: boolean;
  lastMessageAt: string;
  lastMessagePreview?: string | null;
  unreadCount: number;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  body: string;
  sentAt: string;
  readAt?: string | null;
  isMine: boolean;
}

export interface ConversationThread {
  id: string;
  listingId: string;
  listingShortCode: string;
  listingTitle: string;
  listingThumbnailUrl?: string | null;
  listingPrice: number;
  counterpartyId: string;
  counterpartyName: string;
  counterpartyPhotoUrl?: string | null;
  callerIsSeller: boolean;
  messages: ChatMessage[];
}

/* --------------------------------------------------------------------------------------------- */
/* Notifications                                                                                  */
/* --------------------------------------------------------------------------------------------- */

/** 1 new message, 2 expiring, 3 expired, 4 price drop, 5 saved search, 6/7 moderation, 8 review. */
export interface AppNotification {
  id: string;
  type: number;
  title: string;
  body: string;
  link?: string | null;
  createdAt: string;
  readAt?: string | null;
}

export interface NotificationPage {
  items: AppNotification[];
  unreadCount: number;
}

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateListingPayload {
  makeId: number;
  modelId: number;
  variant?: string | null;
  year: number;
  registrationMonth?: number | null;
  mileage: number;
  price: number;
  priceNegotiable: boolean;
  fuelType: number;
  bodyType: number;
  transmission: number;
  driveType: number;
  powerKw?: number | null;
  engineCc?: number | null;
  doors?: number | null;
  seats?: number | null;
  emissionClass?: number | null;
  climate: number;
  interior?: number | null;
  steeringSide: number;
  colour?: string | null;
  metallicPaint: boolean;
  condition: number;
  damageStatus: number;
  origin: number;
  registeredUntil?: string | null;
  previousOwners?: number | null;
  serviceBook: boolean;
  firstOwner: boolean;
  vin?: string | null;
  exchangeAccepted: boolean;
  financeAvailable: boolean;
  vatDeductible: boolean;
  equipmentMask: number;
  cityId: number;
  contactPhone?: string | null;
  description?: string | null;
  publish: boolean;
}
